import { CollectionPaths } from '@order-up/common';
import { Assignment, ENTITY_ID_RANGES, ILocation, ILocationData, ITenant, Location as LocationRecord, Tenant } from '@order-up/models';
import { CreateTenantSchema, CreateTenantType, TenantSchema } from '@order-up/schemas';
import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { DocumentWithId, FirestoreRepository } from '../interfaces/FirestoreRepository';
import { getFirebaseFirestore } from '../firebase/config';
import { createLogger } from '../utils/logger';
import { removeUndefinedValues } from '../utils/firestore';
import { createApiClient } from './apiClient';

const logger = createLogger('TenantService');

const apiClient = createApiClient('/api/v1');

/**
 * Retrieves all tenants accessible to a specific user.
 *
 * Uses the authenticated API endpoint `/users/me/assignments` (backed by the
 * server session cookie, which works even when the Firebase client SDK
 * hasn't finished signing in). With the scope-aware assignment model, the
 * user's accessible tenants are derived from the distinct `tenantDocId`
 * values across all active assignments (tenant- or restaurant-scoped). The
 * tenants collection itself is publicly readable, so the individual tenant
 * documents are then fetched from Firestore.
 *
 * Historically this read the user's Firestore document directly from the
 * client SDK, which required `request.auth != null` — users with only a
 * server session (no Firebase SDK auth yet) got "Missing or insufficient
 * permissions" even though they had valid assignments.
 *
 * @param userId The UID of the user (must match the authenticated session).
 */
const getTenantsByUserId = async (userId: string): Promise<DocumentWithId<Tenant>[]> => {
  logger.debug(`Getting assignments for user: ${userId}`);

  // 1. Fetch the current user's assignments via the API (session-authenticated).
  //    Avoids a Firestore client-SDK read against `users/{uid}`, which fails
  //    with PERMISSION_DENIED when the Firebase client SDK isn't signed in yet.
  let assignments: Assignment[];
  try {
    const response = await apiClient.get('/users/me/assignments') as any;
    const data = response?.data || response;
    assignments = (data?.assignments || []) as Assignment[];
  } catch (error) {
    logger.error('Failed to load assignments from API', {
      error: error instanceof Error ? error.message : String(error),
      userId,
    });
    throw error;
  }

  if (assignments.length === 0) {
    logger.debug(`No assignments found for user: ${userId}`);
    return [];
  }

  // 2. Derive unique tenantDocIds from active assignments.
  const tenantDocIds = Array.from(
    new Set(assignments.filter((a) => a.isActive).map((a) => a.tenantDocId)),
  );
  logger.debug(`Found ${tenantDocIds.length} accessible tenants for user: ${userId}`);

  // 3. Fetch tenant documents in parallel (the tenants collection is publicly readable).
  const tenantRepo = new FirestoreRepository<Tenant>(CollectionPaths.tenants(), Tenant);
  const settled = await Promise.allSettled(
    tenantDocIds.map((tenantDocId) => tenantRepo.getById(tenantDocId)),
  );

  const tenants: DocumentWithId<Tenant>[] = [];
  const failures: Array<{ tenantDocId: string; error: string }> = [];

  settled.forEach((result, index) => {
    const tenantDocId = tenantDocIds[index];
    if (result.status === 'fulfilled') {
      // `null` here is a document that genuinely is not there — a dangling
      // assignment pointing at a retired tenant. That is a DATA problem, not a
      // transport one, and "nothing to open" is the honest answer for it.
      if (result.value) tenants.push(result.value);
      return;
    }
    const error =
      result.reason instanceof Error ? result.reason.message : String(result.reason);
    logger.warn('Failed to load tenant', { tenantDocId, error });
    failures.push({ tenantDocId, error });
  });

  // ⚠️ A FAILED READ IS NOT AN EMPTY RESULT.
  //
  // Every failure used to be caught, logged at WARN, and turned into `null`.
  // The nulls were filtered out and the empty array reached `TenantSelector`,
  // which renders "You are not associated with any tenants." — the exact words
  // shown to someone with no access at all. Its `Error loading organizations`
  // branch was unreachable, because the throw never got there.
  //
  // Measured in prod 2026-09-09: an OWNER with one active, valid assignment on
  // a healthy tenant document read that sentence for ~30 minutes while his
  // session cookie was silently missing. Nothing on screen suggested retrying,
  // so the only move it offered was to doubt the account.
  //
  // Rethrowing only when the list is ENTIRELY empty keeps both other cases
  // honest: a genuinely tenant-less user still gets the empty state, and one
  // unreadable tenant among several still degrades to the ones that loaded
  // rather than blocking the whole chooser.
  if (tenants.length === 0 && failures.length > 0) {
    logger.error('Every tenant read failed — surfacing instead of reporting "no tenants"', {
      userId,
      attempted: tenantDocIds.length,
      failures,
    });
    throw new Error(failures[0].error);
  }

  logger.debug(`Successfully retrieved ${tenants.length} tenants for user: ${userId}`);
  return tenants;
};

/**
 * Creates a new tenant document in Firestore after validating the input data.
 *
 * Counter advance + tenant write run inside a single `runTransaction`.
 * Before this, the two were separate operations: the counter would
 * advance first (`counterService.generateTenantId()`), then the tenant
 * doc would be created. If the doc write failed (Firestore rules,
 * network blip, validation), the counter was permanently ahead of the
 * actual `tenants` collection, leaving "phantom" IDs that
 * `resolveTenantDocId` (in restaurants.ts) couldn't resolve later. The
 * transaction makes those two steps atomic — if the doc write fails,
 * the counter rolls back too.
 *
 * Only `entityType === "tenants"` is migrated here. Other platform
 * counters (restaurants, etc.) keep the older non-atomic path until
 * they're given the same treatment.
 *
 * @param tenantData The raw data for the new tenant conforming to CreateTenantType.
 */
const createTenant = async (tenantData: CreateTenantType): Promise<DocumentWithId<Tenant>> => {
  logger.debug(`Creating new tenant: ${tenantData.name}`);

  // 1. Validate using the creation schema
  const validatedData = CreateTenantSchema.parse(tenantData);
  logger.debug(`Tenant data passed validation for: ${validatedData.name}`);

  // 2. Set up refs outside the transaction. `doc(collectionRef)` with
  //    no second arg returns a ref with a pre-generated auto-ID, so the
  //    write inside the transaction is `tx.set(ref, …)` rather than
  //    `addDoc(...)` (which isn't usable inside a transaction).
  const db = getFirebaseFirestore();
  const tenantCollectionRef = collection(db, CollectionPaths.tenants());
  const newTenantDocRef = doc(tenantCollectionRef);
  const counterDocRef = doc(db, CollectionPaths.platformCounters(), 'tenants');
  const guid = uuidv4();
  const tenantStartId = ENTITY_ID_RANGES.tenants.startId; // 10000001

  // 3. Atomic counter advance + tenant write.
  const { tenantId, payload } = await runTransaction(db, async (tx) => {
    const counterSnap = await tx.get(counterDocRef);

    // Stored `nextId` is semantically "last used id" — see counters.ts
    // `generatePlatformNextId`, which computes the new id as
    // `counter.nextId + 1` and then writes that value back. We match
    // that behavior so existing data keeps incrementing correctly.
    const lastUsed = counterSnap.exists()
      ? ((counterSnap.data() as { nextId?: number }).nextId ?? tenantStartId - 1)
      : tenantStartId - 1;
    const tenantId = lastUsed + 1;

    const fullPayload = removeUndefinedValues({
      tenantId,
      guid,
      name: validatedData.name,
      displayName: validatedData.displayName,
      plan: validatedData.plan || 'starter',
      status: 'active',
      settings: {
        timezone: validatedData.settings?.timezone || 'America/New_York',
        currency: validatedData.settings?.currency || 'USD',
        dateFormat: validatedData.settings?.dateFormat || 'MM/DD/YYYY',
        features: validatedData.settings?.features || [],
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    tx.set(newTenantDocRef, fullPayload);

    if (counterSnap.exists()) {
      tx.update(counterDocRef, {
        nextId: tenantId,
        updatedAt: serverTimestamp(),
      });
    } else {
      // First-ever tenant on a fresh project. Initialize the counter
      // doc with the same shape the rest of the counter service writes
      // (entityType + nextId + timestamps).
      tx.set(counterDocRef, {
        entityType: 'tenants',
        nextId: tenantId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    return { tenantId, payload: fullPayload };
  });

  logger.debug('Tenant created transactionally', { tenantId, docId: newTenantDocRef.id });

  // 4. Shape the return value to match the pre-existing repo.create
  //    contract: `{ ...data, docId }`. Callers (TenantModal, hooks) only
  //    read `tenantId` and `displayName` off it, so the sentinel
  //    timestamps in `payload` are fine.
  return { ...(payload as unknown as Tenant), docId: newTenantDocRef.id } as DocumentWithId<Tenant>;
};

/**
 * Updates an existing tenant document in Firestore after validating the input data.
 * @param guid The GUID of the tenant to update.
 * @param tenantData The partial data for the tenant.
 */
const updateTenant = async (guid: string, tenantData: Partial<ITenant>): Promise<void> => {
  logger.debug(`Updating tenant: ${guid}`);

  // 1. Validate (using .partial() to allow incomplete data)
  const validatedData = TenantSchema.partial().parse(tenantData);
  logger.debug(`Tenant update data passed validation for: ${guid}`);

  // 2. Add updatedAt timestamp and remove undefined values
  const baseUpdatePayload: any = {
    ...validatedData,
    updatedAt: FirestoreRepository.getServerTimestamp(),
  };

  // Remove undefined values before sending to Firestore (matches pattern in all other services)
  const updatePayload = removeUndefinedValues(baseUpdatePayload);

  // 3. Perform Action
  const repo = new FirestoreRepository<Tenant>(CollectionPaths.tenants(), Tenant);
  await repo.updateByQuery([{ field: 'guid', operator: '==', value: guid }], updatePayload);
  logger.debug(`Successfully updated tenant: ${guid}`);
};


/**
 * Retrieves a single tenant by its unique GUID.
 * @param guid The GUID of the tenant.
 */
const getTenantByGuid = async (guid: string): Promise<DocumentWithId<Tenant> | null> => {
  logger.debug(`Getting tenant by guid: ${guid}`);
  const repo = new FirestoreRepository<Tenant>(CollectionPaths.tenants(), Tenant);
  const result = await repo.find([{ field: 'guid', operator: '==', value: guid }], [], 1);
  return result[0] || null;
}

/**
 * Retrieves a single tenant by its numeric tenant ID.
 * @param tenantId The numeric ID of the tenant.
 */
const getTenantById = async (tenantId: number): Promise<DocumentWithId<Tenant> | null> => {
  logger.debug(`Getting tenant by tenantId: ${tenantId}`);
  const repo = new FirestoreRepository<Tenant>(CollectionPaths.tenants(), Tenant);
  const result = await repo.find([{ field: 'tenantId', operator: '==', value: tenantId }], [], 1);
  return result[0] || null;
};

const getAllTenants = async (limit?: number): Promise<DocumentWithId<Tenant>[]> => {
  logger.debug(`Getting all tenants${limit ? ` (limit: ${limit})` : ''}`);
  const tenantRepo = new FirestoreRepository<Tenant>(CollectionPaths.tenants(), Tenant);
  return tenantRepo.find([], [], limit);
};

/**
 * The single, exported service object for all tenant-related database operations.
 */
export const tenantService = {
  createTenant,
  updateTenant,
  getTenantsByUserId,
  getTenantByGuid,
  getTenantById,
  getAllTenants,
};
